import React, { useRef, useCallback, useState } from 'react';
import { Participant } from '../types';
import { compassVariant, discGradient } from './designPreview';
import ViewCone from './ViewCone';

interface CompassWithParticipantsProps {
  yaw: number;
  /** How much of the scene is on screen, left edge of the picture to right
   * edge, in degrees. The CSS cone draws it; the PNG discs cannot, and ignore
   * it. */
  fov: number;
  /** The same, top edge of the picture to bottom. */
  fovVertical: number;
  /** The above or below view, where the cone opens into a circle. */
  overhead: boolean;
  participants: Participant[];
  activeSpeaker: string | null;
  onClickAngle?: (compassDegrees: number) => void;
  parentHeight?: number;
  radarImage?: string;
  /** Explicit diameter in px; wins over the parentHeight rule. */
  size?: number;
}

const POINTER_IMAGE = '/img/pointer.png';
// The disc behind the CSS cone, see-through towards the centre so it is the
// edge that draws the circle. Mono or colour with the cone; both are in
// designPreview.ts. The split halves' View buttons wear it too (Gavin, 11 Oct).
export const CONE_DISC = discGradient();

// The plate is 24% of parent height, clamped between 110px and 250px.
//
// The ceiling and the fraction carry the "make it bigger" ask; the floor
// deliberately does not move. The wheel floats over the picture but sizes off
// the player's HEIGHT, so on a short player the floor is already generous — at
// 392px tall (the admin attribution editor) 110px is 28% of the picture height,
// against 23% fullscreen. Raising the floor would grow the wheel only in the
// mounts that can least afford it, so the extra size comes from MAX_SIZE and
// HEIGHT_FRACTION, which bite on tall players and leave short ones as they are.
const MIN_SIZE = 110;
const MAX_SIZE = 250;
const HEIGHT_FRACTION = 0.24;

// The floor layout, in px at MIN_SIZE: the avatars' orbit, the avatars and
// the radar disc. Gavin, 11 Sep (call): a gap between the viewing cone and
// each avatar's inner side. Gavin, 11 Sep (evening, on the build): "the radar
// should extend to the avatars" — so the glass plate now reaches the avatars'
// OUTER edge and holds them whole, rather than running through their centres.
const MARKER_BASE = 26;
const CONE_GAP_BASE = 6;
// The avatars' orbit is still the old plate's rim; the plate itself grew by
// one avatar to enclose them, so the component's box — what the player lays
// out — is the orbit plus one avatar. Sizes handed in and out of this file
// are that whole footprint, which is now also the plate.
const FOOTPRINT_RATIO = (MIN_SIZE + MARKER_BASE) / MIN_SIZE;

// The height rule (24%, 110–250) sizes the avatars' ORBIT, as it always sized
// the plate, so the faces sit where they did; the footprint adds the plate's
// margin round them.
export const compassSizeFor = (parentHeight?: number): number => {
  const orbit = parentHeight
    ? Math.min(MAX_SIZE, Math.max(MIN_SIZE, Math.round(parentHeight * HEIGHT_FRACTION)))
    : MIN_SIZE;
  return Math.round(orbit * FOOTPRINT_RATIO);
};

const CompassWithParticipants: React.FC<CompassWithParticipantsProps> = ({
  yaw,
  fov,
  fovVertical,
  overhead,
  participants,
  activeSpeaker,
  onClickAngle,
  parentHeight,
  radarImage,
  size,
}) => {
  // `size` overrides the height rule when the player is too short for it —
  // see fitTopRightCluster in layout.ts. Below the floor is allowed there.
  const CONTAINER_SIZE = size ?? compassSizeFor(parentHeight);
  // Everything inside is expressed as a multiple of the floor layout, so the
  // plate, the markers and their orbit all grow together and a marker keeps
  // its bearing at every size.
  const scale = CONTAINER_SIZE / (MIN_SIZE + MARKER_BASE);
  // The plate fills the box: its edge is the avatars' outer edge.
  const PLATE_SIZE = CONTAINER_SIZE;
  const MARKER_SIZE = Math.round(MARKER_BASE * scale);
  // The orbit: each marker's outer edge touches the plate's rim.
  const MARKER_RADIUS = (MIN_SIZE * scale) / 2;
  // The disc stops CONE_GAP short of each marker's inner edge.
  const COMPASS_SIZE = Math.round(2 * (MARKER_RADIUS - MARKER_SIZE / 2 - CONE_GAP_BASE * scale));
  const CENTER = CONTAINER_SIZE / 2;
  // An explicit `radarImage` (the older ?radar=<filename> switch) is drawn the
  // way every ring before the radar discs was: fixed, with the chevron turning.
  const [variant] = useState(compassVariant);
  const artwork = radarImage
    ? { image: radarImage, rotates: false, offset: 0, cone: undefined }
    : variant;
  // Gavin, 16 Sep: the cone design has no radar artwork behind it and no
  // glass plate under it. Since 8 Oct it does have a plain see-through white
  // disc — see below.
  const isCone = artwork.cone === true;
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const onClickAngleRef = useRef(onClickAngle);
  onClickAngleRef.current = onClickAngle;

  const angleFromEvent = useCallback((clientX: number, clientY: number) => {
    const el = containerRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    const dx = clientX - (rect.left + rect.width / 2);
    const dy = clientY - (rect.top + rect.height / 2);
    const angleRad = Math.atan2(dx, -dy);
    return ((angleRad * 180) / Math.PI + 360) % 360;
  }, []);

  // Pointer events, not mouse: Gavin, 3 Nov, "can't turn the radar to change
  // view on iPhone or Android". A finger drag fires no mousemove at all (and
  // only a late, synthetic mousedown on a plain tap), so the mouse-only version
  // could never be turned on a phone. Capturing the pointer keeps the drag
  // ours when the finger leaves the disc; touch-action none stops the browser
  // claiming the drag as a scroll.
  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!onClickAngleRef.current) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();
    isDragging.current = true;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* already gone */ }
    const angle = angleFromEvent(e.clientX, e.clientY);
    if (angle !== null) onClickAngleRef.current(angle);
  }, [angleFromEvent]);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging.current || !onClickAngleRef.current) return;
    const angle = angleFromEvent(e.clientX, e.clientY);
    if (angle !== null) onClickAngleRef.current(angle);
  }, [angleFromEvent]);

  const endDrag = useCallback(() => {
    isDragging.current = false;
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        width: CONTAINER_SIZE,
        height: CONTAINER_SIZE,
        cursor: onClickAngle ? 'pointer' : undefined,
        touchAction: onClickAngle ? 'none' : undefined,
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      {/* The glass. A rounded, translucent, blurred plate behind the radar
          artwork, so the wheel reads as an object resting on the picture rather
          than a decal printed onto it, and so the ring stays legible over a
          bright or busy frame. Decorative only: it never takes the pointer, so a
          drag anywhere across the compass still rotates the view. No border:
          Gavin asked for the thin white line round it to go (11 Sep). The
          tint is light, not dark: a dark tint over a dark frame was "lost
          against black" (Gavin, 11 Sep evening), and it is the blur's frosted
          grey that says "glass". */}
      {!isCone && (
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: (CONTAINER_SIZE - PLATE_SIZE) / 2,
          top: (CONTAINER_SIZE - PLATE_SIZE) / 2,
          width: PLATE_SIZE,
          height: PLATE_SIZE,
          borderRadius: '50%',
          backgroundColor: 'rgba(150, 160, 175, 0.28)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          boxShadow: '0 6px 18px rgba(0, 0, 0, 0.28)',
          pointerEvents: 'none',
        }}
      />
      )}

      {/* The cone. Its box is the avatars' ORBIT, not the component's
          footprint, because the cone's corners are pinned to that orbit —
          see ViewCone. The faces are drawn after it, so an avatar the viewer
          is looking at sits on top of the cone's far end, as in the mock. */}
      {/* The cone's ground. A see-through white disc the size of the cone's
          own sweep, so the radar is set apart from whatever the picture is
          doing underneath it (Gavin's mocks, 8 Oct). Its rim is the avatars'
          orbit, as the cone's far end is. No border and no blur: the mocks
          show the picture through it, lightened towards the rim. */}
      {isCone && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: CENTER - MARKER_RADIUS,
            top: CENTER - MARKER_RADIUS,
            width: MARKER_RADIUS * 2,
            height: MARKER_RADIUS * 2,
            borderRadius: '50%',
            background: CONE_DISC,
            pointerEvents: 'none',
          }}
        />
      )}

      {isCone && (
        <div style={{
          position: 'absolute',
          left: CENTER - MARKER_RADIUS,
          top: CENTER - MARKER_RADIUS,
          pointerEvents: 'none',
        }}>
          <ViewCone yaw={yaw} fov={fov} fovVertical={fovVertical} overhead={overhead} size={MARKER_RADIUS * 2} />
        </div>
      )}

      {/* Centered compass */}
      {!isCone && (
      <div style={{
        position: 'absolute',
        left: (CONTAINER_SIZE - COMPASS_SIZE) / 2,
        top: (CONTAINER_SIZE - COMPASS_SIZE) / 2,
        pointerEvents: 'none',
      }}>
        <div style={{ position: 'relative', width: COMPASS_SIZE, height: COMPASS_SIZE }}>
          {/* The ring. Its own artwork carries the transparency, so it is drawn
              whole — no circular clip, which would shave the outer edge. A
              radar disc turns with the view and needs no chevron: its wedge IS
              the heading. Same negative sign as the chevron, for the reason
              given there. */}
          <img
            src={artwork.image}
            alt="Radar"
            style={{
              width: COMPASS_SIZE,
              height: COMPASS_SIZE,
              display: 'block',
              transform: artwork.rotates ? `rotate(${artwork.offset - yaw}deg)` : undefined,
            }}
          />
          {/* Heading. The chevron is drawn at the top of its own square canvas,
              centred on it, so spinning the whole square about its middle puts
              the chevron where the viewer is looking. Negative, because yaw
              grows anticlockwise while a CSS rotation turns the other way —
              the same sign the FOV wedge this replaced used. */}
          {!artwork.rotates && (
            <img
              src={POINTER_IMAGE}
              alt=""
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                width: COMPASS_SIZE,
                height: COMPASS_SIZE,
                display: 'block',
                opacity: 0.9,
                transform: `rotate(${-yaw}deg)`,
              }}
            />
          )}
        </div>
      </div>
      )}

      {/* Participant markers */}
      {participants.map((p) => {
        const angleDeg = p.position;
        const angleRad = (angleDeg * Math.PI) / 180;
        const x = CENTER + MARKER_RADIUS * Math.sin(angleRad);
        const y = CENTER - MARKER_RADIUS * Math.cos(angleRad);
        const isActive = activeSpeaker != null && p.id === activeSpeaker;
        const initial = (p.name || '?')[0].toUpperCase();

        return (
          <div
            key={p.id}
            onPointerDown={(e) => {
              e.stopPropagation();
              onClickAngle?.(p.position);
            }}
            style={{
              position: 'absolute',
              left: x,
              top: y,
              width: MARKER_SIZE,
              height: MARKER_SIZE,
              borderRadius: '50%',
              transform: 'translate(-50%, -50%)',
              overflow: 'hidden',
              backgroundColor: p.avatar_url ? 'transparent' : p.color,
              color: '#fff',
              fontSize: '11px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: isActive ? `0 0 4px 2px ${p.color}` : 'none',
              border: `2px solid ${p.color}`,
              opacity: isActive ? 1.0 : 0.5,
              transition: 'opacity 0.3s ease',
              cursor: onClickAngle ? 'pointer' : undefined,
            }}
            title={p.name}
          >
            {p.avatar_url ? (
              <img
                src={p.avatar_url}
                alt={p.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }}
              />
            ) : (
              initial
            )}
          </div>
        );
      })}
    </div>
  );
};

export default CompassWithParticipants;
